import {
  LayoutDashboard,
  Users,
  Wallet,
  Landmark,
  Gamepad2,
  Dice5,
  Sparkles,
  Gift,
  Link2,
  ShieldAlert,
  FileBarChart2,
  FileText,
  Bell,
  ClipboardList,
  ListChecks,
  Settings,
  UserSquare2,
  KeyRound,
  LogIn,
  Network,
  Receipt,
  Globe,
  Ticket,
  FlaskConical,
  Images,
  GalleryVerticalEnd,
  Layers,
  SlidersHorizontal,
  CircleDollarSign,
  Palette,
  Shirt,
  type LucideIcon,
} from 'lucide-react';

export interface NavItem {
  /** Route path (absolute). */
  path: string;
  /** i18next key under `nav.*`. */
  labelKey: string;
  /** Optional short section label for grouping. */
  section: 'main' | 'finance' | 'product' | 'ops' | 'system';
  icon: LucideIcon;
  /** Optional permission code required to view this item in the sidebar. */
  permission?: string;
  /**
   * When one code is not the question: ANY of these opens the item.
   *
   * A section whose pages are worked with several different permissions cannot
   * be keyed on one of them — pick the widest and a role that merely READS a
   * record next to the section gets the whole section; pick the narrowest and
   * the people who work it lose their menu. Mutually exclusive with
   * `permission`.
   */
  anyPermission?: readonly string[];
  /**
   * A local-development instrument rather than part of running the platform.
   * One flag, one meaning: the Sidebar shows it only in a dev build to a
   * root-level session, and marks it so nobody mistakes it for an ordinary page.
   * The page behind it must repeat the same check — a nav entry is not a guard.
   */
  devOnly?: true;
  /** i18next key for a one-line "what is behind this link", shown on hover. */
  descriptionKey?: string;
  /**
   * Which live count this item wears as a badge, if any. The Sidebar resolves
   * the name to a hook — a nav item is a data row and must not fetch.
   */
  badge?: 'ticketsQueued';
}

/**
 * The codes that WORK the finance section, one per tab of `FinancePage`.
 *
 * Exported because the page gates its tabs on the same list, item by item: a
 * menu entry whose page then refuses every tab is the shape this replaced.
 */
export const FINANCE_SECTION_PERMISSIONS = [
  // Deposits and Withdraws — deciding a record.
  'deposit:approve',
  'withdraw:approve',
  // Makeup — filing one on a player's behalf.
  'deposit:create',
  'withdraw:create',
  // Bank accounts, Member banks, Reference rates, Display rates.
  'bank_account:read',
  // Approval limits — the brand-wide table, which is the write authority's.
  'approval_limit:update',
] as const;

// Permission codes are the canonical `resource:action` codes from
// @b1fr0st/rbac — the same codes the login response carries and the gateway
// enforces. Each item uses the read permission of the gateway policy behind
// its page's primary API.
export const NAV_ITEMS: NavItem[] = [
  // Every tile on it is a report figure, so it is gated like one. An account
  // without `report:read` used to land here and read a shell whose every
  // request came back 403; it is now sent to the first menu entry it holds.
  { path: '/', labelKey: 'nav.dashboard', section: 'main', icon: LayoutDashboard, permission: 'report:read' },

  { path: '/members', labelKey: 'nav.members', section: 'main', icon: Users, permission: 'member:read' },
  // The CS -> Finance queue. Badged with the tickets nobody has closed yet,
  // refreshed every ten seconds.
  { path: '/tickets', labelKey: 'nav.tickets', section: 'main', icon: Ticket, permission: 'ticket:read', badge: 'ticketsQueued' },
  { path: '/wallet', labelKey: 'nav.wallet', section: 'finance', icon: Wallet, permission: 'wallet:read' },
  // Keyed on WORKING the section, never on reading a record inside it.
  // `deposit:read` is held by CS so a ticket can be linked to the deposit it is
  // about, and keying the menu on it put the brand's entire pending deposit and
  // withdrawal queue — amounts, members, bank cells — one click from a rung
  // whose job is answering customers. These are the codes each tab's own data
  // call needs: decide, file, read the bank rows, read the ceilings.
  {
    path: '/finance',
    labelKey: 'nav.finance',
    section: 'finance',
    icon: Landmark,
    anyPermission: FINANCE_SECTION_PERMISSIONS,
  },

  { path: '/games', labelKey: 'nav.games', section: 'product', icon: Gamepad2, permission: 'game:read' },
  // Recast tiles waiting for a person to approve them before players see them.
  // Read-gated like every menu entry; the approve and redo buttons on the page
  // each ask their own code.
  { path: '/art/games', labelKey: 'nav.artGames', section: 'product', icon: GalleryVerticalEnd, permission: 'art:read' },
  { path: '/art/approvals', labelKey: 'nav.artApprovals', section: 'product', icon: Images, permission: 'art:read' },
  // The model each art step calls and the words it sends. Read-gated; saving
  // asks art:manage on the page, as the gateway does.
  { path: '/art/settings', labelKey: 'nav.artSettings', section: 'product', icon: SlidersHorizontal, permission: 'art:read' },
  { path: '/art/costs', labelKey: 'nav.artCosts', section: 'product', icon: CircleDollarSign, permission: 'art:read' },
  // The Tile Kit every tile follows; editing and releasing it is `art:manage` on the page.
  { path: '/art/kit', labelKey: 'nav.artKit', section: 'product', icon: Palette, permission: 'art:read' },
  // Card themes: saving and binding one is `art:manage`, drawing `art:update`, deciding a card `art:approve`, on the page.
  { path: '/art/themes', labelKey: 'nav.artThemes', section: 'product', icon: Shirt, permission: 'art:read' },
  { path: '/art/batches', labelKey: 'nav.artBatches', section: 'product', icon: Layers, permission: 'art:read' },
  { path: '/betting', labelKey: 'nav.betting', section: 'product', icon: Dice5, permission: 'bet:read' },
  { path: '/promotion', labelKey: 'nav.promotion', section: 'product', icon: Sparkles, permission: 'promotion:read' },
  { path: '/bonus', labelKey: 'nav.bonus', section: 'product', icon: Gift, permission: 'commission:read' },

  { path: '/affiliate', labelKey: 'nav.affiliate', section: 'product', icon: Link2, permission: 'affiliate:read' },

  { path: '/risk', labelKey: 'nav.risk', section: 'ops', icon: ShieldAlert, permission: 'monitor:read' },
  { path: '/reports', labelKey: 'nav.reports', section: 'ops', icon: FileBarChart2, permission: 'report:read' },
  { path: '/content', labelKey: 'nav.content', section: 'ops', icon: FileText, permission: 'notify:read' },
  { path: '/notification', labelKey: 'nav.notification', section: 'ops', icon: Bell, permission: 'notify:read' },
  { path: '/audit', labelKey: 'nav.audit', section: 'ops', icon: ClipboardList, permission: 'audit:read' },
  { path: '/reconcile', labelKey: 'nav.reconcile', section: 'ops', icon: ListChecks, permission: 'reconcile:read' },

  { path: '/organizations', labelKey: 'nav.tenancy', section: 'system', icon: Network, permission: 'tenancy:read' },
  { path: '/site', labelKey: 'nav.mySite', section: 'system', icon: Globe, permission: 'site:manage' },
  { path: '/settlements', labelKey: 'nav.settlements', section: 'system', icon: Receipt, permission: 'settlement:read' },
  { path: '/settings', labelKey: 'nav.settings', section: 'system', icon: Settings, permission: 'setting:read' },
  { path: '/users', labelKey: 'nav.users', section: 'system', icon: UserSquare2, permission: 'admin:read' },
  { path: '/roles', labelKey: 'nav.roles', section: 'system', icon: KeyRound, permission: 'admin:read' },
  // Auth-session manager — list + revoke any user's refresh-token sessions.
  // Level checks (member: any admin / admin: L6 only) are enforced server-side.
  { path: '/auth-sessions', labelKey: 'nav.authSessions', section: 'system', icon: LogIn, permission: 'admin:read' },
  // Operator tooling (simulate a Lao deposit, check a Lao rail's health).
  // Listed unconditionally and filtered at render: this array is evaluated once,
  // when the module first loads, so a condition written here is decided before
  // anything can influence it — including a test.
  {
    path: '/tools',
    labelKey: 'nav.tools',
    section: 'system',
    icon: FlaskConical,
    devOnly: true,
    descriptionKey: 'tools.description',
    // The loosest code any tab needs — each tab gates itself more tightly.
    permission: 'bank_account:read',
  },
];

export const SECTION_ORDER: NavItem['section'][] = ['main', 'finance', 'product', 'ops', 'system'];

/**
 * Every code that opens an item, whichever field carries it. Empty when the
 * item is open to any signed-in account.
 */
export function navPermissions(item: NavItem): readonly string[] {
  if (item.anyPermission?.length) return item.anyPermission;
  return item.permission ? [item.permission] : [];
}

/** Whether this account may open the item at all. */
export function allowsNavItem(
  item: NavItem,
  hasPermission: (code: string) => boolean,
): boolean {
  const codes = navPermissions(item);
  return codes.length === 0 || codes.some(hasPermission);
}
